import {IconButton} from '@wandb/components';
import React, {useState} from 'react';
import {Input, type InputOnChangeData} from 'semantic-ui-react';

import {trackSetting} from '../../services/analytics/workspaceSettingsEvents';
import type * as CustomRunNamesTypes from '../../state/views/customRunNames/types';
import type * as RunSetTypes from '../../state/views/runSet/types';
import {DD_PRIVACY_ALLOW} from '../../util/datadog/privacy';
import {useDebouncedValue} from '../../util/useDebouncedValue';
import {
  type MetricsPickerConfig,
  MetricsRegexToggle,
} from '../elements/MetricsPickerShared';
import {
  keepRegexActionsFromBlurringInput,
  RegexMetricPreview,
} from '../elements/RegexMetricPreview';
import {type RunsLinePlotConfig} from '../PanelRunsLinePlot/types';
import {REGEX_METRIC_MAX_LEN} from '../PanelRunsLinePlot/utils';
import {useTrackSettingPage} from '../WorkspaceDrawer/Settings/hooks/useTrackSettingEvent';
import {MetricSearchErrorNotice} from './MetricSearchErrorNotice';
import {useMetricKeySearch} from './useMetricKeySearch';

const REGEX_DEBOUNCE_MS = 400;

type MetricsRegexInputProps = {
  runSetRefs: RunSetTypes.Ref[];
  customRunNamesRef: CustomRunNamesTypes.Ref | undefined;
  config: MetricsPickerConfig;
  updateConfig: (arg: Partial<RunsLinePlotConfig>) => void;
};

/**
 * NGW replacement for `MetricsPickerRegexInput`. The saved regex is applied
 * server-side (RE2 via `metricSearch`) instead of scanning the retired
 * history-key set: it drives both the match preview here and — via
 * `useRegexMetricsSync` on the panel — the plotted `config.metrics`. This editor
 * only persists `config.metricRegex`.
 */
export const MetricsRegexInput = ({
  runSetRefs,
  customRunNamesRef,
  config,
  updateConfig,
}: MetricsRegexInputProps) => {
  const trackSettingPage = useTrackSettingPage();
  const [debouncedRegex, setDebouncedRegex] = useState(
    config.metricRegex ?? ''
  );

  // Persist only the (debounced) regex. useRegexMetricsSync on the panel
  // resolves it into config.metrics; this editor never writes metrics itself.
  const commitRegex = (value: string) => {
    setDebouncedRegex(value);
    updateConfig({metricRegex: value === '' ? undefined : value});
  };

  const {value: inputRegex, debouncedSetValue: setInputRegex} =
    useDebouncedValue(config.metricRegex ?? '', REGEX_DEBOUNCE_MS, commitRegex);

  const [isPreviewExpanded, setIsPreviewExpanded] = useState(false);
  const onTogglePreviewExpanded = () => {
    setIsPreviewExpanded(prev => !prev);
  };

  const {results, hasMore, loading, error, searchErrorMessage, retry} =
    useMetricKeySearch(runSetRefs, customRunNamesRef, {
      metricNamePattern: debouncedRegex || undefined,
      pageSize: REGEX_METRIC_MAX_LEN,
      skip: debouncedRegex === '',
    });

  const metrics = results.map(r => r.key);
  // The API is paginated with no total, so `count` is the loaded page size;
  // `hasMore` tells the preview to render it as "N+" and show the truncation
  // note (there are more matches server-side than we loaded).
  const preview = {count: metrics.length, metrics};

  const onInputChanged = (
    _: React.SyntheticEvent,
    {value}: InputOnChangeData
  ) => {
    setInputRegex(value);
    setIsPreviewExpanded(value.length > 0);
  };

  const onInputFocused = () => {
    setIsPreviewExpanded(inputRegex.length > 0);
  };

  const onInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.currentTarget.blur();
    }
  };

  const onInputBlurred = () => {
    setIsPreviewExpanded(false);
    trackSetting({
      action: 'change',
      page: trackSettingPage,
      settingLevel: 'panel',
      setting: 'yAxis',
      settingValue: inputRegex,
      additionalProperties: {
        isInputRegexEmpty: inputRegex.length === 0,
      },
    });
  };

  const onClearRegex = () => {
    setInputRegex('');
    setIsPreviewExpanded(false);
  };

  return (
    <div className="flex w-full min-w-0 flex-col gap-12">
      <div className="metrics-picker" data-dd-privacy={DD_PRIVACY_ALLOW}>
        <div className="metrics-picker-regex-input-wrapper">
          <Input
            aria-label="Metric regex"
            style={{flexGrow: 1}}
            className="regex-input"
            loading={loading}
            placeholder="Select metrics with a regular expression"
            value={inputRegex}
            onChange={onInputChanged}
            onFocus={onInputFocused}
            onBlur={onInputBlurred}
            onKeyDown={onInputKeyDown}
          />
          <div
            className="metrics-picker-regex-actions"
            onMouseDown={keepRegexActionsFromBlurringInput}>
            <MetricsRegexToggle config={config} updateConfig={updateConfig} />
            <IconButton
              aria-label="Clear regex input"
              icon="close"
              onPress={onClearRegex}
              size="md"
              variant="ghost"
            />
          </div>
        </div>
      </div>
      {searchErrorMessage != null || error != null ? (
        // A failed search has no match count to show, so the preview would read
        // as "0 matches". Show why it failed instead.
        <MetricSearchErrorNotice
          searchErrorMessage={searchErrorMessage}
          error={error}
          onRetry={retry}
        />
      ) : (
        inputRegex.length > 0 && (
          <RegexMetricPreview
            preview={preview}
            hasMore={hasMore}
            isExpanded={isPreviewExpanded}
            onToggleExpanded={onTogglePreviewExpanded}
          />
        )
      )}
    </div>
  );
};
